import React, { PropsWithChildren, useState } from 'react';
import OpenGraph from '@/components/OpenGraph';
import FullPage from '@/src/components/UI/FullPage';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Flexbox from '@/components/common/Flexbox';
import { FaChevronLeft, FaChevronRight, FaExclamation, FaQuestion } from 'react-icons/fa';
import classNames from 'classnames';
import Image from 'next/image';
import ShowMoreText from 'react-show-more-text';
import SwiperCore, { Navigation as SwiperNavigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { IconType } from 'react-icons';
import ReactIcon from '@/src/components/common/ReactIcon';

const START_FROM_3C = [
  {
    heading: '禮賓計畫 Concierge Program',
    image: '/best-agency/vip.jpg',
    content: `「第一印象的建立，在於初次見面的7秒鐘。」<br/>- 居家清潔<br/>- 精心軟裝布置<br/>- 專業構圖攝影<br/>- 加裝電子鎖`,
  },
  {
    heading: '客製化廣告 Customized AD',
    image: '/best-agency/customized_ad.png',
    content: `數位化的第一步，優化推廣精準行銷<br/>- EDM<br/>- 單一物件文宣<br/>- 策略廣告投放<br/>- 精準媒體露出<br/>- 開辦賞屋 Open House`,
  },
  {
    heading: '市場分析 CMA',
    image: '/best-agency/cma.jpg',
    content: `推薦優勢開價，綜合同區間競品給出開價建議<br/>- 模型演算分析<br/>- 實價登錄<br/>- 市場趨勢<br/>- 在售物件定期追蹤<br/>- 擬定上市價格<br/>- 促進買賣雙方交易及後續流程`,
  },
];

const BestAgency = () => {
  return (
    <React.Fragment>
      <OpenGraph
        title='FUNWOO 德載國際不動產 - 賣屋'
        description='量⾝打造賣屋策略 - 結合最新科技和創新行銷，精準推廣你的房屋，創造最大價值'
        type='funwoo.best-seller'
        url='https://funwoo.com.tw/best-agency'
        imageUrl={`${process.env.NEXT_PUBLIC_GOOGLE_CDN_URL}/largedesktop/hero/agent_v_3.webp`}
      />
      <FullPage hasPadding={false} appContentProps={{ className: 'pt-15' }}>
        <main className={classNames('md:pb-16', 'font-noto')}>
          {/* Section 1 */}
          <Section
            sectionClassName={'bg-gray100'}
            className={classNames(
              'lg:pb-40',
              'md:flex md:items-center md:justify-center md:px-20 md:space-x-4 md:space-y-0',
              'sm:pt-[6rem] sm:pb-28',
              'pt-16 px-6 pb-20 space-y-8'
            )}
          >
            <HighlightBlock wordings={['對於該如何賣屋毫無頭緒？', 'FUNWOO 為你量身打造賣屋策略']} Icon={FaQuestion} />
            <div className={'md:flex-1 md:flex-shrink-0'}>
              <div
                className={classNames(
                  'lg:space-y-[1.125rem]',
                  'md:px-12 md:py-8 md:text-left',
                  'text-center space-y-4'
                )}
              >
                <p className={'text-sm2 text-gray700'}>你是否正在煩惱......</p>
                <div className={'space-y-2'}>
                  <p>初次賣房不知道該怎麼辦？</p>
                  <p>人在海外如何處理國內房產？</p>
                  <p>重購退稅是什麼？要怎麼賣屋才省錢？</p>
                  <p>之前委託賣屋，帶看一堆但都不是精準客戶？</p>
                  <p>掛售超過一年都沒賣掉？</p>
                </div>
              </div>
            </div>
          </Section>
          {/* Section 2 */}
          <Section sectionClassName={'bg-gray100'} className={classNames('lg:pb-40', 'md:px-20', 'px-6 pb-20')}>
            <h2 className={'mb-6 text-2xl text-center font-medium text-gray700'}>房仲科技，從 3C 開始</h2>
            <div className={classNames('md:flex md:space-y-0 md:space-x-4', 'space-y-8')}>
              {START_FROM_3C.map((item) => (
                <StartFrom3C key={item.heading} {...item} />
              ))}
            </div>
          </Section>
          {/* Section 3 */}
          <Section sectionClassName={'bg-gray100'} className={classNames('md:px-20', 'px-6 pb-20 sm:pb-28')}>
            <p className={'mb-8 text-2xl font-medium text-gold text-center'}>
              簡言之，我們將您的愛屋當成一手房來販售
              <br />
              同樣的仲介費，換來更完善的服務，
              <br className={'hidden sm:inline md:hidden'} />
              FUNWOO 是您的不二選擇
            </p>
            <Stories />
          </Section>
          {/* Section 4 */}
          <Section
            sectionClassName={'bg-gray100'}
            className={classNames(
              'md:flex md:items-start md:justify-center md:px-20 md:space-x-4 md:space-y-0',
              'px-6 pb-20 space-y-6'
            )}
          >
            <HighlightBlock
              className={classNames('lg:py-0', 'md:py-8')}
              wordings={['排解疑慮', '你一定還會擔心 ...']}
              Icon={FaExclamation}
            />
            <div className={'md:flex-1 md:flex-shrink-0 '}>
              <div className={classNames('md:px-12 md:py-8', 'space-y-6')}>
                <div className={classNames('md:text-left', 'text-center space-y-2')}>
                  <p>會不會很多人來看，要常常待命？</p>
                  <p>該怎麼開價才合理？</p>
                  <p>到底是賣得快好，還是賣得高好？</p>
                  <p>房屋有哪些地方要整理，會不會花很多錢？</p>
                  <p>好多仲介來問，FUNWOO 能幫我擋掉嗎？</p>
                </div>
                <div className={classNames('md:text-left', 'space-y-2 text-center text-gold font-medium')}>
                  <p>以上問題，我們都有完善的解決方案</p>
                  <p>現在提供免費 & 線上諮詢服務，為您完整說明及評估</p>
                </div>
                <div className={classNames('md:px-0 md:w-40', 'sm:px-10')}>
                  <a href={'/sell#contact-us'} className={'block py-4 w-full bg-gold text-white text-center'}>
                    立即預約
                  </a>
                </div>
              </div>
            </div>
          </Section>
          {/* Section 5 */}
          <Section className={classNames('md:flex md:px-20 md:pt-[7.5rem] md:space-x-4', 'px-6 pt-16')}>
            <div className={classNames('md:flex-[351_351_0%] md:flex-shrink-0 md:mb-0', 'mb-8')}>
              <div className={classNames('md:mb-[-3.875rem]', 'relative mb-4 w-full')}>
                <div className={'pt-[calc((281/327)*100%)]'} />
                <div className={'absolute inset-0'}>
                  <Image src={'/best-agency/form.jpg'} layout={'fill'} objectFit={'cover'} />
                </div>
              </div>
              <div className={classNames('md:relative md:z-10 md:pl-[6.5625rem]')}>
                <div className={classNames('md:px-12 md:text-left', 'py-10 px-6 text-center text-gray700 bg-gray100')}>
                  <p>也許您跟我們諮詢完之後，現在也不是個賣房的好時機</p>
                  <p>但提早評估，總比到時候急著尋找來的好</p>
                  <p>讓我們提供給你最專業的賣房建議</p>
                </div>
              </div>
            </div>
            {/*<div id={'contact-us'} className={'md:flex-[497_497_0%] md:flex-shrink-0'}>*/}
            {/*  <div className={classNames('lg:pl-12 lg:pr-0', 'md:px-6 pt-4')}>*/}
            {/*    <div className={classNames('md:text-left', 'mb-10 space-y-4 text-center')}>*/}
            {/*      <h3 className={'text-gold text-2xl font-medium'}>結合最新科技和創新行銷，創造最大價值</h3>*/}
            {/*      <p className={'text-gray700 text-sm2'}>*/}
            {/*        留下您的聯絡資訊和物件地址，以協助團隊預先製作物件分析報告，供您參考*/}
            {/*      </p>*/}
            {/*    </div>*/}
            {/*    <div className={classNames('md:px-0', 'sm:px-10')}>*/}
            {/*      <BestAgencyConsultantForm />*/}
            {/*    </div>*/}
            {/*  </div>*/}
            {/*</div>*/}
          </Section>
        </main>
      </FullPage>
    </React.Fragment>
  );
};

export default BestAgency;

export const getStaticProps = async ({ locale }: { locale: string }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
    },
  };
};

const Section: React.FC<PropsWithChildren<{ className?: string; sectionClassName?: string }>> = ({
  children,
  className,
  sectionClassName,
}) => {
  return (
    <section className={classNames(sectionClassName)}>
      <div className={classNames(className, 'max-w-[80rem] mx-auto')}>{children}</div>
    </section>
  );
};

const HighlightBlock: React.FC<{
  wordings: Array<string>;
  Icon: IconType;
  className?: string;
}> = ({ wordings, Icon, className }) => {
  return (
    <div
      className={classNames(
        className,
        'md:m-0 md:flex-1 md:flex-shrink-0 md:max-w-none',
        'sm:mb-12 sm:max-w-[32.5rem] sm:mx-auto'
      )}
    >
      <div className={'lg:py-8 lg:px-12'}>
        <Flexbox
          align={'center'}
          justify={'center'}
          className={classNames(
            'sm:relative sm:z-10 sm:mb-[-3.625rem] sm:w-[5.625rem] sm:h-[5.625rem]',
            'text-white bg-gray900 w-16 h-16'
          )}
        >
          <ReactIcon icon={Icon} className={'text-3xl'} />
        </Flexbox>
        <div className={'sm:pl-[4.5rem]'}>
          <div
            className={classNames(
              'sm:px-12 sm:text-xl sm:text-left',
              'py-10 px-6 bg-white space-y-2 text-center font-medium'
            )}
          >
            {wordings.map((wording, index) => (
              <p key={index}>{wording}</p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ImageSection: React.FC<{
  className?: string;
}> = ({ children, className }) => {
  return (
    <div className={classNames('relative mb-6 w-full', className)}>
      <div className={'pt-[calc((220/327)*100%)]'} />
      <div className={'absolute inset-0'}>{children}</div>
    </div>
  );
};

const StartFrom3C: React.FC<(typeof START_FROM_3C)[0]> = ({ heading, image, content }) => {
  return (
    <div className={'md:flex-1'}>
      <ImageSection className={'md:mb-8'}>
        <Image src={image} layout={'fill'} objectFit={'cover'} />
      </ImageSection>
      <div className={classNames('px-3 text-gray900 font-medium')}>
        <p className={'mb-3'}>{heading}</p>
        {/*@ts-ignore*/}
        <ShowMoreText
          className={'text-gray700 w-full'}
          lines={2}
          more={'...顯示更多'}
          less={'顯示較少'}
          anchorClass={'text-gold'}
          truncatedEndingComponent={''}
        >
          <p dangerouslySetInnerHTML={{ __html: content }} />
        </ShowMoreText>
      </div>
    </div>
  );
};

const STORIES = [
  {
    image: '/best-agency/user_story_1.jpg',
    city: '台北市',
    district: '文山區',
    dealAndStartPriceRatio: 97,
    accuracyTarget: 6,
    buyer: '杜太太，美妝品牌代理商',
    story:
      '「這次 FUNWOO 幫我賣房是很特別的經驗，他們有精準投放廣告的專家，透過網路找到最合適的買家，只安排四次看房時段，三個月內就賣出。非常專業、高效。」',
  },
  {
    image: '/best-agency/user_story_2.jpg',
    city: '台北市',
    district: '南港區',
    dealAndStartPriceRatio: 88,
    accuracyTarget: 8,
    buyer: '高先生，教授',
    story:
      '「我的房屋當初要賣的時候是 20 年的原始屋況，非常擔心賣相問題，還好 FUNWOO 有專業的 Staging 宅裝團隊，透過佈置幫我的房子呈現最好的一面，且不需額外加收服務費。」',
  },
  {
    image: '/best-agency/user_story_3.jpg',
    city: '桃園市',
    district: '龜山區',
    dealAndStartPriceRatio: 95,
    accuracyTarget: 6,
    buyer: '李先生',
    story:
      '「他們（FUNWOO）提供完整的物件分析報告幫我快速做定價決定。和過去開高價，慢慢讓買家殺價的過程不一樣，而是追求開價和成交價相近，減少來回不必要的溝通時間和困擾。」',
  },
  {
    image: '/best-agency/user_story_4.jpg',
    city: '台南市',
    district: '安平區',
    dealAndStartPriceRatio: 95,
    accuracyTarget: 5,
    buyer: 'Serina，科技業',
    story:
      '「FUNWOO 是可信任的夥伴，對於委託房屋周遭的生活圈調查仔細，並設想了每個購屋使用者可能需求的特色點，主動出擊投放廣告，為買主找到買屋的初衷，不就是要一個自身需要的好窩？這不就來了嘛，因此加速了買賣過程，彼此皆大歡喜。」',
  },
];

SwiperCore.use([SwiperNavigation]);

const Stories = () => {
  const [swiper, setSwiper] = useState<SwiperCore>();
  return (
    <div className={'relative'}>
      <div className={classNames('md:h-full', 'absolute top-0 left-0 w-full')}>
        <div className={classNames('md:hidden md:invisible', 'pt-[calc((281/327)*100%)]')} />
        <Flexbox align={'center'} justify={'center'} className={classNames('md:-inset-x-9', 'absolute -inset-2')}>
          <Flexbox justify={'between'} className={'w-full'}>
            <Flexbox
              as={'button'}
              type={'button'}
              align={'center'}
              justify={'center'}
              className={classNames(
                'md:h-[4.5rem] md:w-[4.5rem] md:text-3xl',
                'relative z-10 w-12 h-12 bg-white text-gold rounded-full'
              )}
              onClick={() => swiper?.slidePrev()}
            >
              <ReactIcon icon={FaChevronLeft} />
            </Flexbox>
            <Flexbox
              as={'button'}
              type={'button'}
              align={'center'}
              justify={'center'}
              className={classNames(
                'lg:relative lg:right-auto',
                'md:absolute md:right-0 md:h-[4.5rem] md:w-[4.5rem] md:text-3xl',
                'relative z-10 w-12 h-12 bg-white text-gold rounded-full'
              )}
              onClick={() => swiper?.slideNext()}
            >
              <ReactIcon icon={FaChevronRight} />
            </Flexbox>
          </Flexbox>
        </Flexbox>
      </div>
      <Swiper onSwiper={setSwiper} className={'relative z-0'} spaceBetween={0} slidesPerView={1} loop>
        {STORIES.map((item) => (
          <SwiperSlide key={item.image}>
            <StoryItem {...item} />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

const StoryItem: React.FC<(typeof STORIES)[0]> = ({
  image,
  city,
  district,
  dealAndStartPriceRatio,
  accuracyTarget,
  buyer,
  story,
}) => {
  return (
    <div className={'md:flex md:space-x-4'}>
      <div
        className={classNames(
          'lg:flex-[524_524_0%]',
          'md:mb-0 md:flex-shrink-0 md:flex-[349_349_0%] md:h-fit',
          'relative mb-4 w-full'
        )}
      >
        <div
          className={classNames(
            'lg:pt-[calc((450/524)*100%)]',
            'md:pt-[calc((300/349)*100%)]',
            'pt-[calc((281/327)*100%)]'
          )}
        />
        <div className={'absolute inset-0'}>
          <Image src={image} layout={'fill'} objectFit={'cover'} />
        </div>
      </div>
      <div className={classNames('lg:flex-[740_740_0%]', 'md:flex-[497_497_0%]')}>
        <div className={classNames('lg:px-12 lg:mb-8', 'mb-4 py-10 px-6 space-y-2 bg-black')}>
          <p className={'text-xl text-white'}>
            {city} | {district}
          </p>
          <div className={classNames('md:space-x-[4.5rem]', 'flex space-x-6')}>
            <Flexbox direction={'column'} align={'center'} className={classNames('lg:flex-none', 'flex-1 space-y-2')}>
              <p className={'sm:text-6xl text-3xl text-gold font-medium'}>{dealAndStartPriceRatio}%</p>
              <p className={'text-xs text-white'}>成交和開價比</p>
            </Flexbox>
            <Flexbox direction={'column'} align={'center'} className={classNames('lg:flex-none', 'flex-1 space-y-2')}>
              <p className={'sm:text-6xl text-3xl text-gold font-medium'}>{accuracyTarget}</p>
              <p className={'text-xs text-white'}>個精準買家</p>
            </Flexbox>
            <div className={'flex-1'} />
          </div>
        </div>
        <p className={classNames('lg:mb-6', 'md:px-8', 'mb-4')}>{story}</p>
        <p className={classNames('md:px-8', 'text-gray700')}>- {buyer}</p>
      </div>
    </div>
  );
};
